// ==================== Utility Functions ====================

function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour < 6) return 'early_morning';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

function getWeekType() {
  const day = new Date().getDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

async function generateSHA256Hash(inputString) {
  const encoder = new TextEncoder();
  const data = encoder.encode(inputString);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

function getCurrentLanguage() {
  const htmlLang = document.documentElement.lang;
  return htmlLang;
}

function getCurrentCurrency() {
  const currency = document.querySelector('.bucks-selected')?.textContent?.trim();
  return currency || 'AED';
}

function getCustomerInfo() {
  const custIdElem = document.querySelector('.cust-info .cust-id');
  const custStatusElem = document.querySelector('.cust-info .cust-status');
  const customerId = custIdElem ? custIdElem.textContent.trim() : null;
  const customerStatus = custStatusElem ? custStatusElem.textContent.trim() : 'logged_out';
  return { customerId, customerStatus };
}

// ==================== LocalStorage Management ====================
function storeItemTracking(itemId, itemListId, itemListName, index) {
  let existing = {};
  try {
    existing = JSON.parse(localStorage.getItem('ga4_tracking_map') || '{}');
  } catch {
    existing = {};
  }

  existing[itemId] = {
    itemListId,
    itemListName,
    index,
  };

  localStorage.setItem('ga4_tracking_map', JSON.stringify(existing));
}

function getItemTracking(itemId) {
  try {
    const map = JSON.parse(localStorage.getItem('ga4_tracking_map') || '{}');
    if (map[itemId]) {
      return map[itemId];
    }
  } catch {}

  return {
    itemListId: undefined,
    itemListName: undefined,
    index: undefined,
  };
}

// ==================== Item Enrichment ====================

// Helper function to sanitize parameters by converting undefined to null
function sanitizeParams(obj) {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeParams(item));
  }

  const sanitized = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      sanitized[key] = value === undefined ? null : sanitizeParams(value);
    }
  }
  return sanitized;
}

// Helper function to push to dataLayer with sanitized data
function pushToDataLayer(eventData) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(sanitizeParams(eventData));
}

function enrichItem(item, trackingData = null, includeContext = true) {
  const tracking = trackingData || getItemTracking(item.item_id);

  const enrichedItem = {
    ...item,
    item_list_id: tracking.itemListId,
    item_list_name: tracking.itemListName,
    index: Number(tracking.index),
    quantity: item.quantity !== undefined && !isNaN(Number(item.quantity)) ? Number(item.quantity) : null,
  };

  if (includeContext) {
    enrichedItem.time_of_day = getTimeOfDay();
    enrichedItem.week_type = getWeekType();
    enrichedItem.stock_status = item.stock_status || 'in_stock';
  }

  return enrichedItem;
}

function enrichItems(items, includeContext = true) {
  return items.map((item) => enrichItem(item, null, includeContext));
}

// ==================== GA4 Event Functions ====================

function helloBarGA(text, url) {
  pushToDataLayer({
    event: 'hellobar_navigation_click',
    parameters: {
      click_text: text,
      click_url: url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function headerNavigationGA(text, url) {
  pushToDataLayer({
    event: 'header_navigation_click',
    parameters: {
      click_text: text,
      click_url: url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function homeBannerGA(url, banner_name) {
  pushToDataLayer({
    event: 'home_banner_click',
    parameters: {
      click_url: url,
      banner_name: banner_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function ctaButtonGA(section_name, click_text, click_url) {
  pushToDataLayer({
    event: 'cta_button_click',
    parameters: {
      section_name: section_name,
      click_text: click_text,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function viewCollectionCtaGA(section_name, click_url) {
  pushToDataLayer({
    event: 'view_collection_cta_click',
    parameters: {
      section_name: section_name,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function viewAllBlogCtaGA(section_name, click_url) {
  pushToDataLayer({
    event: 'view_all_blog_cta_click',
    parameters: {
      section_name: section_name,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function newsLetterSignupSuccessGA() {
  pushToDataLayer({
    event: 'newsletter_signup_success',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function newsLetterSignupFailedGA(error) {
  pushToDataLayer({
    event: 'newsletter_signup_failed',
    parameters: {
      error_type: error,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function footerNavClickGA(click_text, click_url) {
  pushToDataLayer({
    event: 'footer_navigation_click',
    parameters: {
      click_text: click_text,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function whatsappIconClickGA() {
  pushToDataLayer({
    event: 'whatsapp_icon_click',
    parameters: {
      click_url: 'https://api.whatsapp.com/send?phone=971547017925',
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function socialMediaClickGA(click_text, section_name, click_url) {
  pushToDataLayer({
    event: 'social_media_icon_click',
    parameters: {
      click_text: click_text,
      section_name: section_name,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function colorFilterInteractionGA(filter_value) {
  pushToDataLayer({
    event: 'color_filter_interaction',
    parameters: {
      filter_value: filter_value,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function styleFilterInteractionGA(filter_value) {
  pushToDataLayer({
    event: 'style_filter_interaction',
    parameters: {
      filter_value: filter_value,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function sizeFilterInteractionGA(filter_value) {
  pushToDataLayer({
    event: 'size_filter_interaction',
    parameters: {
      filter_value: filter_value,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function sortbyFilterInteractionGA(filter_value) {
  pushToDataLayer({
    event: 'sortby_filter_interaction',
    parameters: {
      filter_value: filter_value,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function internalLinkClickGA(click_text, click_url, section_name) {
  pushToDataLayer({
    event: 'internal_link_click',
    parameters: {
      click_text: click_text,
      click_url: click_url,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function elitePartnerFormStartGA() {
  pushToDataLayer({
    event: 'elite_partner_form_start',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function elitePartnerFormSubmitGA() {
  pushToDataLayer({
    event: 'elite_partner_form_submit',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function elitePartnerFormSubmitFailedGA(error) {
  pushToDataLayer({
    event: 'elite_partner_form_submit_failed',
    parameters: {
      error_type: error,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function elitePartnerFormSubmitSuccessGA() {
  pushToDataLayer({
    event: 'elite_partner_form_submit_success',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function loginWithShopFailedGA(error) {
  pushToDataLayer({
    event: 'login_with_shop_failed',
    parameters: {
      error_type: error,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithShopSucceededGA() {
  pushToDataLayer({
    event: 'login_with_shop_succeeded',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithGoogleStartedGA() {
  pushToDataLayer({
    event: 'login_with_google_started',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithGoogleFailedGA(error) {
  pushToDataLayer({
    event: 'login_with_google_failed',
    parameters: {
      error_type: error,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithGoogleSucceededGA() {
  pushToDataLayer({
    event: 'login_with_google_succeeded',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithGoogleSucceededGA() {
  pushToDataLayer({
    event: 'login_with_google_succeeded',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithEmailIdStartedGA() {
  pushToDataLayer({
    event: 'login_with_email_id_started',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function otpEntryStartGA() {
  pushToDataLayer({
    event: 'otp_entry_start',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function otpVerificationFailedGA() {
  pushToDataLayer({
    event: 'otp_verification_failed',
    parameters: {
      error_type: 'Invalid or expired OTP',
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function loginWithEmailSucceededGA() {
  pushToDataLayer({
    event: 'login_with_email_succeeded',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
    },
  });
}

function userProfileUpdatedGA(form_status, error_type) {
  pushToDataLayer({
    event: 'user_profile_updated',
    parameters: {
      form_status: form_status,
      error_type: error_type,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function addressUpdateSuccessGA(form_status, error_type) {
  pushToDataLayer({
    event: 'address_update_success',
    parameters: {
      form_status: form_status,
      error_type: error_type,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function addNewAddressSuccessGA(form_status, error_type) {
  pushToDataLayer({
    event: 'add_new_address_success',
    parameters: {
      form_status: form_status,
      error_type: error_type,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function addressRemovedGA() {
  pushToDataLayer({
    event: 'address_removed',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function signOutEverywhereClickGA() {
  pushToDataLayer({
    event: 'sign_out_everywhere_click',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function blogPostSelectGA(blog_name, blog_url, section_name) {
  pushToDataLayer({
    event: 'blog_post_select',
    parameters: {
      blog_name: blog_name,
      blog_url: blog_url,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function blogShareClickGA(blog_name, share_platform, section_name) {
  pushToDataLayer({
    event: 'blog_share_click',
    parameters: {
      blog_name: blog_name,
      share_platform: share_platform,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function breadcrumbLinkClickGA(click_text, click_url, section_name) {
  pushToDataLayer({
    event: 'breadcrumb_link_click',
    parameters: {
      click_text: click_text,
      click_url: click_url,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function findUsSidebarOpenedGA() {
  pushToDataLayer({
    event: 'find_us_sidebar_opened',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function viewStoreLinkClickGA(click_url, section_name) {
  pushToDataLayer({
    event: 'view_store_link_click',
    parameters: {
      click_url: click_url,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function howItWorksCtaClickGA(click_url, section_name) {
  pushToDataLayer({
    event: 'how_it_works_cta_click',
    parameters: {
      click_url: click_url,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function add_to_free_trial_plp(product_id, product_name) {
  pushToDataLayer({
    event: 'TBYB_collection_add_to_try_button',
    parameters: {
      product_id: product_id,
      product_name: product_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function bookFreeHomeTrialCtaClickGA(click_text, section_name, click_url) {
  pushToDataLayer({
    event: 'book_free_home_trial_cta_click',
    parameters: {
      click_text: click_text,
      section_name: section_name,
      click_url: click_url,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function startFreeHomeTrialClickGA(click_text, section_name, click_url) {
  pushToDataLayer({
    event: 'start_free_home_trial_click',
    parameters: {
      click_url: click_url,
      section_name: section_name,
      click_text: click_text,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function readyToTryButtonClickGA(products) {
  pushToDataLayer({
    event: 'ready_to_try_button_click',
    parameters: {
      products: products,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function step1HomeTrialProductAddedGA(product_name, product_id, product_category, section_name) {
  pushToDataLayer({
    event: 'step_1_home_trial_product_added',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function step2ScheduleYourFreeTrialGA(products) {
  pushToDataLayer({
    event: 'step_2_schedule_your_free_trial',
    parameters: {
      products: products,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

// Builds the `user_data` block for the trial funnel events from the contact
// details captured on the scheduling step. email + phone are SHA-256-hashed
// before they hit the dataLayer (phone as E.164); name + address are passed as
// entered. Same shape as the KH storefront (KH-182) so GTM variables line up.
// Returns undefined when nothing was supplied so the event still fires.
async function trialUserData(contact) {
  if (!contact) return undefined;
  const { name, email, phone, address } = contact;
  if (!name && !email && !phone && !address) return undefined;
  try {
    const [emailHash, phoneHash] = await Promise.all([
      email ? generateSHA256Hash(email) : Promise.resolve(''),
      phone ? generateSHA256Hash(phone) : Promise.resolve(''),
    ]);
    const userData = {};
    if (name) userData.name = name;
    if (email) userData.email = emailHash;
    if (phone) userData.phone_number = phoneHash;
    if (address) userData.address = address;
    return userData;
  } catch {
    return undefined;
  }
}

async function step3DateTimeSelectedGA(emirate_selected, selected_date, selected_time, form_status, contact) {
  const user_data = await trialUserData(contact);
  pushToDataLayer({
    event: 'step_3_date_time_selected',
    parameters: {
      emirate_selected: emirate_selected,
      selected_date: selected_date,
      selected_time: selected_time,
      form_status: form_status,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
    ...(user_data ? { user_data } : {}),
  });
}

function step4HomeTrialDetailsConfirmedGA() {
  pushToDataLayer({
    event: 'step_4_home_trial_details_confirmed',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function bookFreeHomeTrialCtaPDPGA(product_name, product_id, product_category) {
  pushToDataLayer({
    event: 'book_free_home_trial_cta_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function videoStartGA(video_title, video_url, video_provider, section_name) {
  pushToDataLayer({
    event: 'video_start',
    parameters: {
      video_title: video_title,
      video_url: video_url,
      video_provider: video_provider,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function videoProgressGA(video_title, video_url, video_provider, section_name, video_progress) {
  pushToDataLayer({
    event: 'video_progress',
    parameters: {
      video_title: video_title,
      video_url: video_url,
      video_provider: video_provider,
      section_name: section_name,
      video_progress: video_progress,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function videoCompleteGA(video_title, video_url, video_provider, section_name) {
  pushToDataLayer({
    event: 'video_complete',
    parameters: {
      video_title: video_title,
      video_url: video_url,
      video_provider: video_provider,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function faqInteractionGA(faq_question, action_type) {
  pushToDataLayer({
    event: 'faq_interaction',
    parameters: {
      faq_question: faq_question,
      action_type: action_type,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function productShareClickGA(product_name, product_id, product_category, share_platform) {
  pushToDataLayer({
    event: 'product_share_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      share_platform: share_platform,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function reviewFormStartGA(product_name, product_id, product_category) {
  pushToDataLayer({
    event: 'review_form_start',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function contactFormStartGA() {
  pushToDataLayer({
    event: 'contact_form_start',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function contactFormSubmitGA() {
  pushToDataLayer({
    event: 'contact_form_submit',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function contactFormSubmitSuccessGA() {
  pushToDataLayer({
    event: 'contact_form_submit',
    parameters: {
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function contactFormSubmitFailedGA(error) {
  pushToDataLayer({
    event: 'contact_form_submit_failed',
    parameters: {
      error_type: error,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function preOrderNowCtaClickGA(product_name, product_id, product_category, section_name) {
  pushToDataLayer({
    event: 'pre_order_now_cta_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function cartIconClickGA(total_items, section_name) {
  pushToDataLayer({
    event: 'cart_icon_click',
    parameters: {
      total_items: Number(total_items),
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function userProfileIconClickGA(section_name) {
  pushToDataLayer({
    event: 'user_profile_icon_click',
    parameters: {
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function wishlistIconClickGA(total_items, section_name) {
  pushToDataLayer({
    event: 'wishlist_icon_click',
    parameters: {
      total_items: total_items,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function accordionMenuInteractionGA(product_name, product_id, product_category, selected_accordion, action_type) {
  pushToDataLayer({
    event: 'accordion_menu_interaction',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      selected_accordion: selected_accordion,
      action_type: action_type,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function discoverMoreCtaClickGA() {
  pushToDataLayer({
    event: 'discover_more_cta_click',
    parameters: {
      section_name: 'Wishlist',
      click_url: '/',
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function productVariantSelectGA(selected_variant, product_name, product_id, product_category) {
  pushToDataLayer({
    event: 'product_variant_select',
    parameters: {
      selected_variant: selected_variant,
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function productSizeSelectGA(selected_variant, product_name, product_id, product_category) {
  pushToDataLayer({
    event: 'product_size_select',
    parameters: {
      selected_variant: selected_variant,
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function sizeBarClickGA(product_name, product_id, product_category, click_text, section_name) {
  pushToDataLayer({
    event: 'size_bar_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      click_text: click_text,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function reviewPostClickGA(section_name, review_title, rating) {
  pushToDataLayer({
    event: 'review_post_click',
    parameters: {
      section_name: section_name,
      review_title: review_title,
      rating: rating,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function colorSelectionGA(product_name, product_id, product_category, selected_color, page_type, section_name) {
  if (section_name === '') {
    if (window.location.href.indexOf('search') !== -1) {
      section_name = 'Search Results';
    }
    if (window.location.href.indexOf('products') !== -1) {
      section_name = product_name;
    }
  }
  pushToDataLayer({
    event: 'color_selection',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      selected_color: selected_color,
      page_type: page_type,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function notifyWhenBackInStockClickGA(product_name, product_id, product_category, section_name) {
  pushToDataLayer({
    event: 'notify_when_back_in_stock_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function reviewClickGA(product_name, product_id, product_category, section_name) {
  pushToDataLayer({
    event: 'review_click',
    parameters: {
      product_name: product_name,
      product_id: product_id,
      product_category: product_category,
      section_name: section_name,
      language: getCurrentLanguage(),
      currency: getCurrentCurrency(),
      user_status: getCustomerInfo().customerStatus,
      user_id: getCustomerInfo().customerId,
    },
  });
}

function viewItemListGA(id, name, items) {
  const itemsWithTracking = items.map((item, index) => ({
    ...item,
    index: item.index !== undefined ? item.index : index + 1,
    item_list_id: id,
    item_list_name: name,
  }));

  pushToDataLayer({
    event: 'view_item_list',
    ecommerce: {
      item_list_id: id,
      item_list_name: name,
      language: getCurrentLanguage(),
      items: itemsWithTracking,
    },
  });
}

function selectItemGA(id, name, item) {
  if (item.item_id) {
    storeItemTracking(item.item_id, item.item_list_id, item.item_list_name, item.index);
  }

  if (item.price) {
    item.price = Number(item.price);
  }
  if (item.index) {
    item.index = Number(item.index);
  }

  pushToDataLayer({
    event: 'select_item',
    ecommerce: {
      item_list_id: id,
      item_list_name: name,
      language: getCurrentLanguage(),
      items: [item],
    },
  });
}

function viewCartGA(value, items) {
  if (items && items.length) {
    items = items.map((item) => {
      if (item.price) {
        item.price = Number(item.price);
      }
      return item;
    });
  }

  if (items && items.length) {
    items = items.map((item) => {
      if (item.index) {
        item.index = Number(item.index);
      }
      return item;
    });
  }

  const enrichedItems = items.map((item) => enrichItem(item, false));

  pushToDataLayer({
    event: 'view_cart',
    ecommerce: {
      language: getCurrentLanguage(),
      currency: 'AED',
      value: value,
      items: enrichedItems,
    },
  });
}

function addToWishlistGA(section_name, value, item) {
  if (item && item.price) {
    item.price = Number(item.price);
  }

  if (item && item.index) {
    item.index = Number(item.index);
  }

  item.quantity = 1;

  pushToDataLayer({
    event: 'add_to_wishlist',
    ecommerce: {
      section_name: section_name,
      currency: 'AED',
      value: Number(value),
      language: getCurrentLanguage(),
      items: [item],
    },
  });
}

function removeFromCartGA(value, item) {
  if (item.price) {
    item.price = Number(item.price);
  }
  if (item.index) {
    item.index = Number(item.index);
  }

  const enrichedItem = enrichItem(item, false);

  pushToDataLayer({
    event: 'remove_from_cart',
    ecommerce: {
      language: getCurrentLanguage(),
      currency: 'AED',
      value: value,
      items: [enrichedItem],
    },
  });
}
